import { useAppBridge } from '@shopify/app-bridge-react'
import {
  BlockStack,
  Button,
  ComplexAction,
  Divider,
  InlineStack,
  Modal,
  ModalProps,
} from '@shopify/polaris'
import React from 'react'

const ActionButton = ({
  action,
  key,
  primary,
}: {
  action: ComplexAction
  key: React.Key
  primary: boolean
}) => (
  <Button
    key={key}
    onClick={action.onAction}
    loading={action.loading}
    disabled={action.disabled}
    tone={action.destructive ? 'critical' : undefined}
    variant={primary ? 'primary' : undefined}
  >
    {action.content}
  </Button>
)

/**
 * Fixes mobile app footer being too close to the bottom navigation bar,
 * forwarding `primaryAction` / `secondaryActions` to the native footer.
 */
const AdaptiveModalBase = ({
  primaryAction,
  secondaryActions,
  footer,
  children,
  ...rest
}: ModalProps) => {
  const shopify = useAppBridge()
  const isMobile = shopify?.environment?.mobile === true
  const hasActions = Boolean(primaryAction || secondaryActions?.length)

  if (!isMobile || (!hasActions && !footer)) {
    return (
      <Modal
        primaryAction={primaryAction}
        secondaryActions={secondaryActions}
        footer={footer && <div className="AdaptiveModal-Footer">{footer}</div>}
        {...rest}
      >
        {children}
      </Modal>
    )
  }

  return (
    <Modal
      {...rest}
      footer={
        <div className="AdaptiveModal-Footer" style={{ paddingBottom: '20px' }}>
          <BlockStack gap="400">
            {footer}
            {hasActions && (
              <>
                {footer && <Divider />}
                <div className="AdaptiveModal-FooterActions">
                  <InlineStack gap="300" align="end">
                    {secondaryActions?.map((a, i) => (
                      <ActionButton key={`secondary-${i}`} action={a} primary={false} />
                    ))}
                    {primaryAction && <ActionButton key="primary" action={primaryAction} primary />}
                  </InlineStack>
                </div>
              </>
            )}
          </BlockStack>
        </div>
      }
    >
      {children}
    </Modal>
  )
}

export const AdaptiveModal = Object.assign(AdaptiveModalBase, { Section: Modal.Section })
